import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useDebounce } from '@hooks/useDebounce';
import './Autocomplete.css';

interface AutocompleteProps<T> {
  label: string;
  placeholder: string;
  value: T | null;
  onSelect: (item: T | null) => void;
  searchFn: (query: string) => Promise<T[]>;
  getDisplayValue: (item: T) => string;
  getKey: (item: T) => number;
  disabled?: boolean;
  required?: boolean;
}

export default function Autocomplete<T>({
  label,
  placeholder,
  value,
  onSelect,
  searchFn,
  getDisplayValue,
  getKey,
  disabled = false,
  required = false,
}: AutocompleteProps<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      return;
    }

    const search = async () => {
      setIsLoading(true);
      try {
        const data = await searchFn(debouncedQuery);
        setResults(data);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedQuery, searchFn]);

  const handleSelect = (item: T) => {
    onSelect(item);
    setQuery('');
    setIsOpen(false);
    setResults([]);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="autocomplete-field">
      <label>
        {label}
        {required && <span className="required">*</span>}
      </label>
      {value ? (
        <div className="autocomplete-selected">
          <span>{getDisplayValue(value)}</span>
          {!disabled && (
            <button type="button" className="clear-btn" onClick={handleClear} title="Clear selection" aria-label="Clear selection">
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        <div className="autocomplete-input-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder={placeholder}
            aria-label={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
          />
          {isOpen && (query.length >= 2 || results.length > 0) && (
            <div className="autocomplete-dropdown">
              {isLoading ? (
                <div className="autocomplete-loading">Searching...</div>
              ) : results.length > 0 ? (
                results.map((item) => (
                  <div
                    key={getKey(item)}
                    className="autocomplete-item"
                    onClick={() => handleSelect(item)}
                  >
                    {getDisplayValue(item)}
                  </div>
                ))
              ) : query.length >= 2 ? (
                <div className="autocomplete-empty">No results found</div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
